<template>
  <b-container class="container-rda">
    <b-row>
      <b-col>
        <Breadcrumb :breadcrumb="breadcrumb" />
      </b-col>
    </b-row>

    <b-row v-if="teacher">
      <b-col>
        <h2 class="purple bold">Notificações</h2>
        <p class="teacher-name">{{ teacher.User.name }}</p>
      </b-col>

      <b-col class="text-right">
        <b-button
          class="btn large blue"
          style="max-height: 50px;"
          @click="openCreateModal"
          >Nova notificação</b-button
        >
      </b-col>
    </b-row>

    <b-row v-if="teacher">
      <b-col>
        <DataTable
          :items="notifications"
          :fields="fields"
          :busy="!isRequestSend"
          :row-class="deadlineStatusClass"
          clickable
          empty-text="Nenhuma notificação encontrada"
          class="list-top"
          @row-clicked="viewNotification"
        >
          <template #cell(message)="{ item }">
            <div class="message-cell">
              <p class="message-text" :title="item.message">
                {{ truncatedMessage(item.message) }}
              </p>
              <div class="cell-meta">
                <span>{{ formatDate(item.created_at) }}</span>
              </div>
            </div>
          </template>

          <template #cell(student)="{ item }">
            <div class="student-cell">
              <p class="student-name">{{ studentName(item) }}</p>
              <div class="cell-meta">
                <span v-if="item.Subject">{{ item.Subject.designation }}</span>
                <span v-if="item.Cycle" class="badge badge-ciclo">{{
                  item.Cycle.name
                }}</span>
              </div>
            </div>
          </template>

          <template #cell(deadline)="{ item }">
            <p>{{ item.deadline ? formatDeadline(item.deadline) : "-" }}</p>
          </template>

          <template #cell(status)="{ item }">
            <span class="status-tag" :class="notificationStatusClass(item)">
              {{ notificationStatus(item) }}
            </span>
          </template>
        </DataTable>

        <b-row class="align-center" v-if="count > 0 && isRequestSend">
          <b-pagination
            v-model="page"
            :per-page="perPage"
            :total-rows="count"
            @change="changePage"
            aria-controls="notificationsList"
            last-text
            first-text
          >
            <template #prev-text>
              <LeftArrowIcon class="left" />
            </template>
            <template #next-text>
              <LeftArrowIcon />
            </template>
          </b-pagination>
        </b-row>
      </b-col>
    </b-row>

    <b-row v-else-if="loadError">
      <b-col class="text-center mt-50">
        <p>Ocorreu um problema ao carregar o Professor</p>
        <p>Por favor, atualize a pagina e tente novamente.</p>
      </b-col>
    </b-row>

    <TeacherNotificationCreate
      :teacher="teacher"
      :notification="editingNotification"
      @created="onNotificationCreated"
      @updated="onNotificationUpdated"
      @closed="onCreateModalClosed"
    />
    <TeacherNotificationDetail
      :teacher="teacher"
      :notification="selectedNotification"
      @edit="onEditNotification"
      @deleted="onNotificationDeleted"
    />
  </b-container>
</template>

<script>
import moment from "moment-timezone";
import httpRda from "../../../http-rda";
import TeacherNotificationsService from "@/services/teacherNotifications.service";
import LeftArrowIcon from "@/components/svg/LeftArrowIcon.vue";
import TeacherNotificationCreate from "@/components/modals/TeacherNotificationCreate.vue";
import TeacherNotificationDetail from "@/components/modals/TeacherNotificationDetail.vue";
import DataTable from "@/components/table/DataTable.vue";
import { deadlineState } from "@/utils/notificationDeadline";

export default {
  name: "professor-notificacoes",

  components: {
    LeftArrowIcon,
    TeacherNotificationCreate,
    TeacherNotificationDetail,
    DataTable,
  },

  data() {
    return {
      teacherId: null,
      teacher: null,
      loadError: false,

      notifications: [],
      isRequestSend: false,
      selectedNotification: null,
      editingNotification: null,

      page: 1,
      perPage: 10,
      count: 0,

      fields: [
        {
          key: "message",
          label: "Mensagem",
          thStyle: { width: "45%" },
          tdStyle: { width: "45%" },
        },
        {
          key: "student",
          label: "Aluno",
          thStyle: { width: "25%" },
          tdStyle: { width: "25%" },
        },
        { key: "deadline", label: "Prazo" },
        { key: "status", label: "Status" },
      ],

      breadcrumb: [],
    };
  },

  methods: {
    async fetchTeacher() {
      try {
        const data = await httpRda
          .get(`/teachers/${this.teacherId}/edit`)
          .then((response) => response.data.teacher);

        this.teacher = {
          id: data.id,
          User: data.User,
          Cycles: data.cycles || [],
          Subjects: data.subjects || [],
        };
      } catch (error) {
        this.loadError = true;
      }
    },

    async fetchNotifications() {
      this.isRequestSend = false;

      try {
        const response = await TeacherNotificationsService.getAll({
          teacher_id: this.teacherId,
          page: this.page,
          pageSize: this.perPage,
        });

        this.notifications = response.data.notifications;
        this.count = response.data.count;
      } catch (error) {
        this.notifications = [];
        this.count = 0;
      }

      this.isRequestSend = true;
    },

    changePage(page) {
      this.page = page;
      this.fetchNotifications();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    truncatedMessage(message) {
      return message.length > 80 ? message.substring(0, 80) + "..." : message;
    },

    studentName(notification) {
      const name =
        notification.Student && notification.Student.User
          ? notification.Student.User.name
          : "";
      return name || "---";
    },

    notificationStatus(notification) {
      if (notification.resolved_at > 0) {
        return "Resolvida";
      }
      if (notification.readed_at > 0) {
        return "Lida";
      }

      return "Pendente";
    },

    notificationStatusClass(notification) {
      if (notification.resolved_at > 0) {
        return "status-resolved";
      }
      if (notification.readed_at > 0) {
        return "status-read";
      }

      return "status-pending";
    },

    onNotificationCreated(notification) {
      this.notifications.unshift(notification);
      this.count++;
    },

    onNotificationUpdated(notification) {
      const index = this.notifications.findIndex(
        (item) => item.id === notification.id
      );

      if (index !== -1) {
        this.notifications.splice(index, 1, notification);
      }
    },

    onNotificationDeleted(notificationId) {
      const index = this.notifications.findIndex(
        (item) => item.id === notificationId
      );

      if (index !== -1) {
        this.notifications.splice(index, 1);
        this.count--;
      }
    },

    onEditNotification(notification) {
      this.editingNotification = notification;
      this.$bvModal.show("new-teacher-notification");
    },

    openCreateModal() {
      this.editingNotification = null;
      this.$bvModal.show("new-teacher-notification");
    },

    onCreateModalClosed() {
      this.editingNotification = null;
    },

    viewNotification(notification) {
      this.selectedNotification = notification;
      this.$bvModal.show("view-teacher-notification");
    },

    formatDate(date) {
      return moment.tz(date, "America/Sao_Paulo").format("DD/MM/YYYY HH:mm");
    },

    formatDeadline(date) {
      return moment.tz(date, "YYYY-MM-DD").format("DD/MM/YYYY");
    },

    deadlineStatusClass(notification) {
      const state = deadlineState(notification);

      if (state === "overdue") {
        return "row-danger";
      }

      if (state === "warning") {
        return "row-warning";
      }

      return "";
    },

    buildBreadcrumb() {
      this.breadcrumb = [
        {
          text: "Coordenação",
          href: "/coordenacao",
        },
        {
          text: "Estatísticas dos Professores",
          href: "/coordenacao/professores",
        },
        {
          text: this.teacher ? this.teacher.User.name : "Notificações",
          href: `/coordenacao/professores/${this.teacherId}/notificacoes`,
          active: true,
        },
      ];
    },
  },

  async created() {
    this.teacherId = this.$route.params.id;
    const routeTeacher = this.$route.params.teacher;

    if (routeTeacher && routeTeacher.User) {
      this.teacher = routeTeacher;
    } else {
      await this.fetchTeacher();
    }

    this.buildBreadcrumb();

    if (this.teacher) {
      this.fetchNotifications();
    }
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/variables.scss";

.teacher-name {
  color: $grey4;
  margin-top: 4px;
}

.list-top {
  margin-top: 30px;
  margin-bottom: 30px;
}

.message-cell,
.student-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.message-text,
.student-name {
  color: $grey3;
  font-weight: 600;
  line-height: 1.4;
  font-size: 14px;
}

.cell-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  color: $grey4;
  font-size: 13px;

  .badge {
    font-size: 12px;
    padding: 2px 10px;
  }
}

.status-tag {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 6px;
  color: $white;
  font-weight: $bold;
  font-size: 14px;
  line-height: 20px;

  &.status-pending {
    background-color: $yellow;
    color: $grey1;
  }

  &.status-read {
    background-color: $grey4;
  }

  &.status-resolved {
    background-color: $green1;
  }
}
</style>
